<script lang="ts">
  /**
   * Cap tall content at a readable height and offer the rest on one click.
   *
   * NOT `line-clamp`. That needs plain text, and the thing being capped here is a
   * rendered post body — links, mentions, paragraphs, images. The card that wanted a
   * short preview used to swap the RICH body for a stripped-to-plain one, which is a
   * different piece of content wearing the same clothes: markdown showing through,
   * mentions as raw `[@Name](obj://…)`. So this caps by HEIGHT and renders the real
   * thing either way.
   *
   * IT ONLY INTERVENES WHEN IT HAS TO. Content under the cap gets no button, no fade
   * and no wrapper behaviour at all — the overwhelming majority of comments are two
   * lines and must not pay for the one that is forty.
   *
   * The measurement re-runs on resize AND on load, because an image arriving late is
   * exactly the case where a first measurement says "fits" and the truth is "does not".
   */
  import type { Snippet } from 'svelte';

  let {
    max = 260,
    label = 'Show more',
    children,
  }: {
    /** Height in px to cap at. Roughly ten lines of body text at default sizing. */
    max?: number;
    /** What the expand control says. The collapse control is always "Show less". */
    label?: string;
    children: Snippet;
  } = $props();

  let el = $state<HTMLElement | null>(null);
  let overflows = $state(false);
  let open = $state(false);

  $effect(() => {
    const node = el;
    if (!node) return;
    const measure = (): void => {
      // Only meaningful while collapsed: expanded, `scrollHeight === clientHeight`
      // and it would measure itself back to "fits".
      if (!open) overflows = node.scrollHeight > max + 24;
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(node);
    // Late-loading media changes the answer, and `load` does not bubble.
    node.addEventListener('load', measure, true);
    return () => {
      ro.disconnect();
      node.removeEventListener('load', measure, true);
    };
  });
</script>

<div
  bind:this={el}
  class="relative {overflows && !open ? 'overflow-hidden' : ''}"
  style={overflows && !open ? `max-height:${max}px` : ''}
>
  {@render children()}
  {#if overflows && !open}
    <!-- The fade is the affordance: a hard cut reads as the end of the text, and
         someone who thinks they have read it all will not look for a button. -->
    <div class="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-base-100 to-transparent"></div>
  {/if}
</div>

{#if overflows}
  <button
    type="button"
    class="mt-1 self-start text-left text-xs font-medium text-base-content/62 underline-offset-2
           transition-colors hover:text-primary hover:underline"
    onclick={() => (open = !open)}
  >{open ? 'Show less' : label}</button>
{/if}
